import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import {
  Loader2,
  Database,
  Search,
  X,
  Store as StoreIcon,
  ChevronRight,
  Filter,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import type { StoreListItem, IngestionConfig } from './types';
import { STATUS_CONFIG } from './types';
import { InfoBadge } from '../../components/ui/Tooltip';

import shopifyLogo from '../../assets/platforms/shopify.svg';
import metaLogo from '../../assets/platforms/meta.svg';
import googleAdsLogo from '../../assets/platforms/google-ads.svg';
import tiktokLogo from '../../assets/platforms/tiktok.svg';
import tiktokShopLogo from '../../assets/platforms/tiktok-shop.svg';
import pinterestLogo from '../../assets/platforms/pinterest.svg';
import klaviyoLogo from '../../assets/platforms/klaviyo.svg';
import amazonAdsLogo from '../../assets/platforms/amazon-ads.svg';
import amazonSellerLogo from '../../assets/platforms/amazon-seller.svg';
import ga4Logo from '../../assets/platforms/ga4.svg';

// ─── Platform metadata ──────────────────────────

const PLATFORM_ICONS: Record<string, string> = {
  shopify: shopifyLogo,
  meta: metaLogo,
  google_ads: googleAdsLogo,
  tiktok_business: tiktokLogo,
  tiktok_shop: tiktokShopLogo,
  pinterest: pinterestLogo,
  klaviyo: klaviyoLogo,
  amazon_ads: amazonAdsLogo,
  amazon_seller: amazonSellerLogo,
  ga4: ga4Logo,
};

const PLATFORM_LABELS: Record<string, string> = {
  shopify: 'Shopify',
  meta: 'Meta',
  google_ads: 'Google',
  tiktok_business: 'TikTok',
  tiktok_shop: 'TT Shop',
  pinterest: 'Pinterest',
  klaviyo: 'Klaviyo',
  amazon_ads: 'Amz Ads',
  amazon_seller: 'Amz Seller',
  ga4: 'GA4',
};

const FILTER_OPTIONS = ['All', 'Has Errors', 'Stale', 'No Configs', 'Healthy'] as const;
type FilterOption = (typeof FILTER_OPTIONS)[number];

const SORT_OPTIONS = ['Name', 'Last Activity', 'Config Count'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

// ─── Helpers ────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function isStale(cfg: IngestionConfig): boolean {
  if (cfg.status !== 'active') return false;
  const last = cfg.lastStoreActiveAt ?? cfg.lastScheduledAt ?? cfg.lastBackfillAt;
  if (!last) return true;
  return Date.now() - new Date(last).getTime() > 24 * 60 * 60 * 1000;
}

function lastActivityTime(cfgs: IngestionConfig[]): string | null {
  let latest: string | null = null;
  for (const c of cfgs) {
    const t = c.lastStoreActiveAt ?? c.lastScheduledAt ?? c.lastBackfillAt;
    if (t && (!latest || t > latest)) latest = t;
  }
  return latest;
}

type StoreHealth = 'healthy' | 'stale' | 'errored' | 'no-configs';

function storeHealth(cfgs: IngestionConfig[]): StoreHealth {
  if (cfgs.length === 0) return 'no-configs';
  if (cfgs.some((c) => c.status === 'disabled')) return 'errored';
  if (cfgs.some(isStale)) return 'stale';
  return 'healthy';
}

const HEALTH_DOT: Record<StoreHealth, string> = {
  healthy: 'bg-green-500',
  stale: 'bg-amber-500',
  errored: 'bg-red-500',
  'no-configs': 'bg-gray-300',
};

// ─── Main Component ─────────────────────────────

export function IngestionStoreList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>('All');
  const [sortBy, setSortBy] = useState<SortOption>('Name');
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [focusIdx, setFocusIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const { data: stores, isLoading } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreListItem[]>('/api/v1/admin/stores', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load stores');
      return res.data;
    },
  });

  const { data: configs } = useQuery({
    queryKey: ['admin', 'ingestion', 'configs'],
    queryFn: async ({ signal }) => {
      const res = await api.get<IngestionConfig[]>('/api/v1/ingestion/admin/configs', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load configs');
      return res.data;
    },
    refetchInterval: 15_000,
  });

  const configsByStore = useMemo(() => {
    const map = new Map<number, IngestionConfig[]>();
    for (const c of configs ?? []) {
      const list = map.get(c.storeId) ?? [];
      list.push(c);
      map.set(c.storeId, list);
    }
    return map;
  }, [configs]);

  // ─── Global summary ───────────────────────────

  const globalSummary = useMemo(() => {
    const all = configs ?? [];
    return {
      total: all.length,
      active: all.filter((c) => c.status === 'active').length,
      backfilling: all.filter((c) => c.status === 'backfilling').length,
      paused: all.filter((c) => c.status === 'paused').length,
      errored: all.filter((c) => c.status === 'disabled').length,
      stale: all.filter(isStale).length,
    };
  }, [configs]);

  // ─── Filter + Sort + Search ───────────────────

  const resultStores = useMemo(() => {
    let list = [...(stores ?? [])];

    // Filter
    if (filter === 'Has Errors') {
      list = list.filter((s) => storeHealth(configsByStore.get(s.id) ?? []) === 'errored');
    } else if (filter === 'Stale') {
      list = list.filter((s) => storeHealth(configsByStore.get(s.id) ?? []) === 'stale');
    } else if (filter === 'No Configs') {
      list = list.filter((s) => !configsByStore.has(s.id) || (configsByStore.get(s.id) ?? []).length === 0);
    } else if (filter === 'Healthy') {
      list = list.filter((s) => storeHealth(configsByStore.get(s.id) ?? []) === 'healthy');
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          String(s.id).includes(q) ||
          (s.owner?.email.toLowerCase().includes(q) ?? false),
      );
    }

    // Sort
    if (sortBy === 'Last Activity') {
      list.sort((a, b) => {
        const ta = lastActivityTime(configsByStore.get(a.id) ?? []);
        const tb = lastActivityTime(configsByStore.get(b.id) ?? []);
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return tb.localeCompare(ta);
      });
    } else if (sortBy === 'Config Count') {
      list.sort((a, b) => (configsByStore.get(b.id)?.length ?? 0) - (configsByStore.get(a.id)?.length ?? 0));
    } else {
      // Name — but prioritize stores with configs
      list.sort((a, b) => {
        const aHas = configsByStore.has(a.id) ? 0 : 1;
        const bHas = configsByStore.has(b.id) ? 0 : 1;
        if (aHas !== bHas) return aHas - bHas;
        return a.name.localeCompare(b.name);
      });
    }

    return list;
  }, [stores, configs, configsByStore, filter, search, sortBy]);

  // ─── Keyboard navigation ──────────────────────

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (resultStores.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((prev) => Math.min(prev + 1, resultStores.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && focusIdx >= 0 && focusIdx < resultStores.length) {
        navigate(`/ingestion/${resultStores[focusIdx]!.id}`);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [resultStores, focusIdx, navigate]);

  useEffect(() => {
    if (focusIdx < 0) return;
    const el = listRef.current?.querySelector(`[data-idx="${focusIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusIdx]);

  // ─── Seed configs ─────────────────────────────

  async function handleSeedConfigs() {
    setSeeding(true);
    setSeedResult(null);
    const res = await api.post('/api/v1/ingestion/admin/seed-configs', {});
    if (res.success && res.data) {
      const d = res.data as { createdCount: number; skippedCount: number };
      setSeedResult(`Created ${d.createdCount} configs (${d.skippedCount} already existed)`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'ingestion', 'configs'] });
    } else {
      setSeedResult(res.error ?? 'Failed to seed configs');
    }
    setSeeding(false);
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">Ingestion</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {globalSummary.total} configs across {configsByStore.size} stores
            {globalSummary.active > 0 && <span className="text-green-600 dark:text-green-400"> &middot; {globalSummary.active} active</span>}
            {globalSummary.stale > 0 && <span className="text-amber-600"> &middot; {globalSummary.stale} stale</span>}
            {globalSummary.errored > 0 && <span className="text-red-600 dark:text-red-400"> &middot; {globalSummary.errored} errored</span>}
          </p>
          {seedResult && <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{seedResult}</p>}
        </div>
        <button
          onClick={handleSeedConfigs}
          disabled={seeding}
          className="px-4 py-2 text-sm font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-2"
        >
          {seeding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Seed Configs
        </button>
      </div>

      {/* Global Summary Cards */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {([
          { label: 'Active', value: globalSummary.active, color: 'text-green-600 dark:text-green-400', filterTo: 'Healthy' as const },
          { label: 'Backfilling', value: globalSummary.backfilling, color: 'text-blue-600 dark:text-blue-400', filterTo: 'All' as const },
          { label: 'Paused', value: globalSummary.paused, color: 'text-amber-600', filterTo: 'All' as const },
          { label: 'Stale (>24h)', value: globalSummary.stale, color: globalSummary.stale > 0 ? 'text-amber-600' : 'text-gray-400 dark:text-gray-500', filterTo: 'Stale' as const },
          { label: 'Errored', value: globalSummary.errored, color: globalSummary.errored > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500', filterTo: 'Has Errors' as const },
        ]).map((card) => (
          <button
            key={card.label}
            onClick={() => setFilter(filter === card.filterTo && filter !== 'All' ? 'All' : card.filterTo)}
            className={`bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm ${
              filter === card.filterTo && filter !== 'All' ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
            }`}
          >
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">{card.label}</div>
            <div className={`text-lg font-semibold ${card.color}`}>{card.value}</div>
          </button>
        ))}
      </div>

      {/* Search + Filter + Sort */}
      {(stores ?? []).length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stores by name, ID, or owner..."
              className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  filter === opt
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'border-gray-200 text-gray-500 dark:text-gray-400 hover:bg-gray-50'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="text-xs border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1.5 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>Sort: {opt}</option>
            ))}
          </select>
        </div>
      )}

      {/* Store List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-60 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-40" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (stores ?? []).length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <Database className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No stores found.</p>
        </div>
      ) : (
        <div ref={listRef} className="space-y-1.5">
          {resultStores.map((store, idx) => {
            const storeConfigs = configsByStore.get(store.id) ?? [];
            const health = storeHealth(storeConfigs);
            const lastAct = lastActivityTime(storeConfigs);
            const configuredCount = storeConfigs.length;
            const connectedCount = store.integrations?.length ?? 0;
            const isFocused = focusIdx === idx;

            return (
              <div
                key={store.id}
                data-idx={idx}
                onClick={() => navigate(`/ingestion/${store.id}`)}
                className={`bg-white dark:bg-gray-900 rounded-xl border px-4 py-3 cursor-pointer hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm transition-all ${
                  isFocused ? 'ring-2 ring-gray-900 ring-offset-1 border-gray-300' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Store icon with health dot */}
                    <div className="relative w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                      <StoreIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <InfoBadge tip={
                        health === 'healthy' ? 'All configs are active and pulling data on schedule.' :
                        health === 'stale' ? 'At least one active config has not pulled data in over 24 hours.' :
                        health === 'errored' ? 'One or more configs are disabled, likely due to a connection issue.' :
                        'No ingestion configs exist for this store yet.'
                      }>
                        <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${HEALTH_DOT[health]}`} />
                      </InfoBadge>
                    </div>

                    <div className="min-w-0">
                      {/* Store name + ID + owner */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{store.name}</span>
                        <span className="text-gray-400 dark:text-gray-500 text-xs font-normal">#{store.id}</span>
                        {store.owner?.email && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-[150px]">{store.owner.email}</span>
                        )}
                      </div>

                      {/* Platform config pills with icons */}
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {storeConfigs.length > 0 ? (
                          storeConfigs.map((c) => {
                            const s = STATUS_CONFIG[c.status] ?? STATUS_CONFIG['active']!;
                            const icon = PLATFORM_ICONS[c.platform];
                            const label = PLATFORM_LABELS[c.platform] ?? c.platform;
                            const staleFlag = isStale(c);
                            return (
                              <InfoBadge key={c.id} tip={`${label} -- ${s.label}${staleFlag ? ' (stale -- no data in 24h+)' : ''}`}>
                                <span
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${s.className} ${
                                    staleFlag ? 'border-l-2 border-l-amber-400' : ''
                                  }`}
                                >
                                  {icon && <img src={icon} alt="" className="w-3 h-3" />}
                                  {label}
                                </span>
                              </InfoBadge>
                            );
                          })
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">No ingestion configs</span>
                        )}
                      </div>

                      {/* Meta row: last activity + config/connected counts */}
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400 dark:text-gray-500">
                        {lastAct && <span>Last activity: {timeAgo(lastAct)}</span>}
                        {connectedCount > 0 && (
                          <InfoBadge tip={`${configuredCount} out of ${connectedCount} connected platforms have ingestion configs. Missing configs won't pull data automatically.`}>
                            <span>
                              {configuredCount}/{connectedCount} platforms configured
                            </span>
                          </InfoBadge>
                        )}
                        {health === 'stale' && (
                          <InfoBadge tip="This store has at least one active ingestion config that hasn't pulled data in over 24 hours. This may indicate a connection issue or scheduling problem.">
                            <span className="text-amber-600 flex items-center gap-0.5">
                              <AlertTriangle className="w-2.5 h-2.5" /> Stale
                            </span>
                          </InfoBadge>
                        )}
                        {health === 'healthy' && storeConfigs.length > 0 && (
                          <InfoBadge tip="All ingestion configs for this store are active and have pulled data within the last 24 hours.">
                            <span className="text-green-600 dark:text-green-400 flex items-center gap-0.5">
                              <CheckCircle2 className="w-2.5 h-2.5" /> Healthy
                            </span>
                          </InfoBadge>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                </div>
              </div>
            );
          })}
          {resultStores.length === 0 && (search || filter !== 'All') && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No stores matching {search ? `"${search}"` : ''} {filter !== 'All' ? `(${filter})` : ''}
              </p>
              <button
                onClick={() => { setSearch(''); setFilter('All'); }}
                className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
